import { useAbortController } from '@packages/core/http';
import { useEffect, useState } from 'react';

/** Looks up the result of a key; the signal aborts the lookup. */
export type Lookup<TKey, TResult> = (key: TKey, signal: AbortSignal) => Promise<TResult>;

/**
 * The result of a lookup keyed by a value: run again whenever the key changes, aborted when the key
 * changes or the component unmounts, and forgotten in between. A lookup that fails leaves no result.
 *
 * @param key - what to look up, or undefined to look nothing up; a primitive, compared by value
 * @param lookup - the lookup; a module-level function, so its identity never changes
 * @returns the result once looked up; undefined without a key, while looking up, or when the lookup failed
 */
export function useLookup<TKey, TResult>(key: TKey | undefined, lookup: Lookup<TKey, TResult>): TResult | undefined {
    const { abortSignalRef, signalAbort } = useAbortController();
    const [result, setResult] = useState<TResult>();

    useEffect(() => {
        setResult(undefined);

        if (key === undefined) {
            return;
        }

        const signal = abortSignalRef.current;

        lookup(key, signal)
            .then((found) => {
                if (!signal.aborted) {
                    setResult(found);
                }
            })
            .catch(() => undefined);

        return signalAbort;
    }, [abortSignalRef, key, lookup, signalAbort]);

    return result;
}
